import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../assets/styles/CareersPage.css';

interface JobListing {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
}

const CareersPage: React.FC = () => {
  // Sample job listings data
  const jobListings: JobListing[] = [
    {
      id: 1,
      title: "Senior Machine Learning Engineer",
      department: "Engineering",
      location: "Orlando, FL (Hybrid)",
      type: "Full-time",
      description: "Join our core AI team to enhance and expand our Price on Rails predictive technology. You'll work on cutting-edge algorithms that analyze market patterns and project potential price movements.",
      requirements: [
        "5+ years of experience in machine learning engineering or research",
        "Strong Python skills and familiarity with ML frameworks (TensorFlow, PyTorch)",
        "Experience with time series forecasting and financial market data",
        "MS or PhD in Computer Science, Statistics, or related field",
        "Knowledge of quantitative finance concepts is a plus"
      ],
      responsibilities: [
        "Develop and improve ML models for real-time price prediction",
        "Implement efficient data processing pipelines for market data",
        "Collaborate with product and UX teams to integrate AI capabilities",
        "Research and implement novel approaches to time series forecasting",
        "Contribute to our ML infrastructure and deployment systems"
      ]
    },
    {
      id: 2,
      title: "Frontend Developer (React/TypeScript)",
      department: "Engineering",
      location: "Remote (US)",
      type: "Full-time",
      description: "Help build our intuitive trading interface that visualizes complex market data and AI-powered predictions. You'll work closely with UX designers and backend engineers to create responsive, performant user experiences.",
      requirements: [
        "3+ years of experience with React and TypeScript",
        "Strong understanding of modern JavaScript and browser APIs",
        "Experience with data visualization libraries (D3.js, Chart.js)",
        "Knowledge of state management patterns and responsive design",
        "Familiarity with WebSocket APIs and real-time data"
      ],
      responsibilities: [
        "Implement and optimize interactive chart components",
        "Build responsive dashboard interfaces for different devices",
        "Collaborate with UX designers to implement intuitive trading flows",
        "Optimize performance for real-time data visualization",
        "Write unit and integration tests for frontend components"
      ]
    },
    {
      id: 3,
      title: "Product Marketing Manager",
      department: "Marketing",
      location: "Orlando, FL (Hybrid)",
      type: "Full-time",
      description: "Lead go-to-market strategies for our AI trading solutions. You'll work closely with product, sales, and customer success teams to position our platform in the competitive fintech landscape.",
      requirements: [
        "4+ years of experience in product marketing, preferably in fintech",
        "Strong understanding of financial markets and trading concepts",
        "Excellent communication and presentation skills",
        "Experience creating customer-facing content and sales enablement materials",
        "Bachelor's degree in Marketing, Business, or related field"
      ],
      responsibilities: [
        "Develop positioning and messaging for Price on Rails and Agentic Bot",
        "Create marketing materials including case studies, white papers, and demos",
        "Work with content team to create educational materials for traders",
        "Analyze market trends and competitive landscape",
        "Collaborate with product teams on feature launches and updates"
      ]
    },
    {
      id: 4,
      title: "Quantitative Research Analyst",
      department: "Research",
      location: "Orlando, FL (Hybrid)",
      type: "Full-time",
      description: "Research and develop new quantitative strategies to enhance our predictive algorithms. You'll analyze market data and patterns to identify new signals that can improve our forecasting accuracy.",
      requirements: [
        "MS or PhD in Finance, Mathematics, Statistics, or related field",
        "Experience with statistical analysis and financial modeling",
        "Programming skills in Python, R, or similar languages",
        "Knowledge of machine learning concepts and applications",
        "Understanding of market microstructure and order book dynamics"
      ],
      responsibilities: [
        "Research and develop new quantitative signals for price prediction",
        "Backtest strategies and evaluate performance metrics",
        "Collaborate with ML engineers to implement promising approaches",
        "Stay current with academic research in quantitative finance",
        "Document methodologies and findings for internal knowledge base"
      ]
    }
  ];

  const [activeTab, setActiveTab] = useState<string>('all');
  const [selectedJob, setSelectedJob] = useState<JobListing | null>(null);
  
  // Filter jobs by department
  const filteredJobs = activeTab === 'all' 
    ? jobListings 
    : jobListings.filter(job => job.department.toLowerCase() === activeTab);
  
  // Get unique departments for filter tabs
  const departments = ['all', ...new Set(jobListings.map(job => job.department.toLowerCase()))];

  return (
    <>
      <Helmet>
        <title>Careers at Translate Stocks | Join Our Team</title>
        <meta name="description" content="Join our team at Translate Stocks and help shape the future of AI-powered trading technology. Explore open positions and our company culture." />
        <meta name="keywords" content="translate stocks careers, ai trading jobs, fintech careers, machine learning jobs" />
      </Helmet>
      
      <Header />
      
      <main className="careers-page">
        <section className="hero-section careers-hero">
          <div className="container">
            <div className="hero-content">
              <h1>Build the <span className="highlight">Future of Trading</span> With Us</h1>
              <p className="hero-subtitle">
                We're a team of traders, engineers, and AI researchers united by a mission to transform 
                how the world makes trading decisions. Join us on this journey.
              </p>
            </div>
          </div>
        </section>
        
        <section className="careers-culture">
          <div className="container">
            <div className="culture-grid">
              <div className="culture-content">
                <h2>Our Culture</h2>
                <p>
                  At Translate Stocks, we're building a culture where innovation thrives, diverse perspectives 
                  are valued, and everyone has the opportunity to make an impact. We combine the energy and 
                  flexibility of a startup with the stability and resources needed to solve complex problems.
                </p>
                <div className="culture-values">
                  <div className="culture-value">
                    <h3>Continuous Learning</h3>
                    <p>We're in a rapidly evolving field that demands constant learning and adaptation. We support 
                    professional development and encourage exploration of new ideas.</p>
                  </div>
                  <div className="culture-value">
                    <h3>Collaborative Innovation</h3>
                    <p>Our best ideas come from cross-functional collaboration. Engineers, traders, researchers, 
                    designers—everyone brings unique insights that drive our innovations.</p>
                  </div>
                  <div className="culture-value">
                    <h3>Work-Life Balance</h3>
                    <p>We believe in sustainable high performance. Flexible work arrangements, generous time off, 
                    and focus on results rather than hours help our team stay energized and creative.</p>
                  </div>
                </div>
              </div>
              <div className="culture-image">
                <img 
                  src="/images/office-culture.jpg" 
                  alt="Translate Stocks office environment" 
                  width="540" 
                  height="400"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>
        
        <section className="careers-benefits">
          <div className="container">
            <h2 className="section-title">Benefits & Perks</h2>
            
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM24 40C15.18 40 8 32.82 8 24C8 15.18 15.18 8 24 8C32.82 8 40 15.18 40 24C40 32.82 32.82 40 24 40ZM25 14H22V26L32.5 32.5L34 29.84L25 24.5V14Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Flexible Work</h3>
                <p>Hybrid work options with flexible hours to accommodate your productivity peaks</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M32 12V8H16V12H4V40H44V12H32ZM20 12H28V16H20V12ZM40 36H8V16H16V20H32V16H40V36Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Competitive Compensation</h3>
                <p>Salary, equity, and bonuses that reflect your contributions to our success</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M40 12H36V8C36 6.9 35.1 6 34 6H14C12.9 6 12 6.9 12 8V12H8C6.9 12 6 12.9 6 14V40C6 41.1 6.9 42 8 42H40C41.1 42 42 41.1 42 40V14C42 12.9 41.1 12 40 12ZM16 8H32V12H16V8ZM38 38H10V16H38V38Z" fill="#2563EB"/>
                    <path d="M24 28C26.21 28 28 26.21 28 24C28 21.79 26.21 20 24 20C21.79 20 20 21.79 20 24C20 26.21 21.79 28 24 28ZM24 22C25.1 22 26 22.9 26 24C26 25.1 25.1 26 24 26C22.9 26 22 25.1 22 24C22 22.9 22.9 22 24 22Z" fill="#2563EB"/>
                    <path d="M18 34H30V32C30 29.34 24.67 28 24 28C23.33 28 18 29.34 18 32V34Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>Top-Tier Healthcare</h3>
                <p>Comprehensive medical, dental, and vision coverage for you and your dependents</p>
              </div>
              
              <div className="benefit-card">
                <div className="benefit-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M24 4C12.95 4 4 12.95 4 24C4 35.05 12.95 44 24 44C35.05 44 44 35.05 44 24C44 12.95 35.05 4 24 4ZM24 40C15.18 40 8 32.82 8 24C8 15.18 15.18 8 24 8C32.82 8 40 15.18 40 24C40 32.82 32.82 40 24 40ZM30 30H18C16.9 30 16 29.1 16 28V16C16 14.9 16.9 14 18 14H30C31.1 14 32 14.9 32 16V28C32 29.1 31.1 30 30 30ZM20 20V24H28V20H20Z" fill="#2563EB"/>
                  </svg>
                </div>
                <h3>401(k) Matching</h3>
                <p>Generous employer match to help you save for the future</p>
              </div>
            </div>
          </div>
        </section>
        
        <section className="open-positions">
          <div className="container">
            <h2 className="section-title">Open Positions</h2>
            
            <div className="positions-filter">
              {departments.map((dept) => (
                <button
                  key={dept}
                  className={activeTab === dept ? 'active' : ''}
                  onClick={() => setActiveTab(dept)}
                >
                  {dept === 'all' ? 'All Departments' : dept}
                </button>
              ))}
            </div>
            
            <div className="positions-container">
              {selectedJob ? (
                <div className="job-details">
                  <button 
                    className="back-button"
                    onClick={() => setSelectedJob(null)}
                  >
                    ← Back to listings
                  </button>
                  
                  <div className="job-header">
                    <h3>{selectedJob.title}</h3>
                    <div className="job-meta">
                      <span className="job-department">{selectedJob.department}</span>
                      <span className="job-location">{selectedJob.location}</span>
                      <span className="job-type">{selectedJob.type}</span>
                    </div>
                  </div>
                  
                  <div className="job-description">
                    <p>{selectedJob.description}</p>
                  </div>
                  
                  <div className="job-section">
                    <h4>Responsibilities</h4>
                    <ul>
                      {selectedJob.responsibilities.map((resp, index) => (
                        <li key={index}>{resp}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="job-section">
                    <h4>Requirements</h4>
                    <ul>
                      {selectedJob.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="job-apply">
                    <a href={`/apply/${selectedJob.id}`} className="btn btn-primary btn-large">Apply Now</a>
                    <a href={`mailto:careers@translatestocks.com?subject=Application for ${selectedJob.title}`} className="btn btn-outline">
                      Email Your Resume
                    </a>
                  </div>
                </div>
              ) : (
                <div className="jobs-list">
                  {filteredJobs.length > 0 ? (
                    filteredJobs.map((job) => (
                      <div key={job.id} className="job-card" onClick={() => setSelectedJob(job)}>
                        <h3>{job.title}</h3>
                        <div className="job-meta">
                          <span className="job-department">{job.department}</span>
                          <span className="job-location">{job.location}</span>
                          <span className="job-type">{job.type}</span>
                        </div>
                        <p className="job-summary">{job.description.substring(0, 120)}...</p>
                        <button className="view-job">View Position</button>
                      </div>
                    ))
                  ) : (
                    <div className="no-jobs">
                      <p>No open positions in this department at the moment. Please check back later or explore other departments.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
        
        <section className="careers-cta">
          <div className="container">
            <div className="cta-wrapper">
              <h2>Don't see the right position?</h2>
              <p>We're always looking for exceptional talent. Send your resume to our recruitment team and tell us how you can contribute.</p>
              <a href="mailto:careers@translatestocks.com" className="btn btn-primary btn-large">Contact Recruiting Team</a>
            </div>
          </div>
        </section>
      </main>
      
    </>
  );
};

export default CareersPage;